// Pedido de cancelamento pelo Portal do Consumidor (#5833, projeto 11).
//
// A gravação é toda no servidor (`abrir_cancelamento_portal`, migration
// 20260923190000): o consumidor não escreve em solicitacoes_cancelamento, que
// está fechada desde a demanda 02. O que vive aqui é só a regra de tela — quais
// unidades podem pedir e o que explicar quando não podem.
//
// Sem I/O nas funções puras; o acesso ao banco fica no fim do arquivo.

export type MotivoPortal =
  | 'placas_proprias' | 'outra_empresa' | 'desconto_baixo' | 'mudanca_endereco'
  | 'imovel_vendido' | 'unidade_desativada' | 'empresa_encerrada'
  | 'inadimplencia' | 'insatisfacao' | 'financeiro' | 'outro';

/** Os 5 motivos que o sistema já usa (decisão de 23/09/2026, até a demanda 01 definir a lista nova). */
/**
 * Lista de 26/09/2026, na ordem do que o cliente mais escolhe. Veio da leitura
 * dos 101 pedidos que estavam como 'outro' (59,8% do total): o motivo mais
 * comum era justamente o que não existia na lista — placas solares próprias,
 * 24 casos.
 *
 * Texto na voz do cliente, não na do sistema: quem lê é ele, no celular.
 * 'outro' fica por último e exige que ele escreva o porquê — é o que impede
 * esta lista de virar a antiga de novo.
 */
export const MOTIVOS_PORTAL: Array<{ valor: MotivoPortal; label: string }> = [
  { valor: 'placas_proprias', label: 'Instalei (ou vou instalar) placas solares próprias' },
  { valor: 'outra_empresa', label: 'Troquei por outra empresa de energia' },
  { valor: 'desconto_baixo', label: 'O desconto foi menor do que eu esperava' },
  { valor: 'mudanca_endereco', label: 'Mudei de endereço ou não moro mais aqui' },
  { valor: 'imovel_vendido', label: 'Vendi o imóvel ou o titular mudou' },
  { valor: 'unidade_desativada', label: 'A unidade foi desativada ou demolida' },
  { valor: 'empresa_encerrada', label: 'A empresa encerrou as atividades' },
  { valor: 'inadimplencia', label: 'Tenho faturas em atraso' },
  { valor: 'insatisfacao', label: 'Não estou satisfeito com o atendimento' },
  { valor: 'financeiro', label: 'Motivo financeiro' },
  { valor: 'outro', label: 'Outro motivo' },
];

export interface UcDoPortal {
  id: number;
  numero_uc: string | null;
  endereco?: string | null;
  situacao?: string | null;
  empresa_parceira_id?: string | null;
}

export interface PedidoEmAberto {
  uc_id: number;
  protocolo: string | null;
  status: string;
  data_solicitacao: string | null;
  data_retorno_prevista: string | null;
}

export type ImpedimentoUc = 'de_parceira' | 'ja_cancelada' | 'pedido_em_aberto';

export interface UcParaPedido extends UcDoPortal {
  podePedir: boolean;
  impedimento: ImpedimentoUc | null;
  /** Texto para o cliente — sem jargão e sem culpar o cadastro. */
  aviso: string | null;
  pedidoEmAberto: PedidoEmAberto | null;
}

/**
 * Situações em que a UC já está cancelada e não há o que pedir. Comparação sem
 * caixa e sem espaço em volta: a situação vem de cadastro digitado e de planilha,
 * onde "cancelada " e "CANCELADA" aparecem.
 */
const SITUACOES_ENCERRADAS = ['cancelada', 'cancelado', 'inativa'];

const situacaoEncerrada = (v: string | null | undefined): boolean =>
  SITUACOES_ENCERRADAS.includes(String(v ?? '').trim().toLowerCase());

/**
 * Decide, para cada unidade do cliente, se ela pode entrar num pedido novo.
 * O servidor confere tudo de novo — isto existe para a tela explicar antes.
 */
export function prepararUcsDoPedido(ucs: UcDoPortal[], pedidos: PedidoEmAberto[]): UcParaPedido[] {
  const abertoPorUc = new Map<number, PedidoEmAberto>();
  for (const p of pedidos || []) {
    if (p && (p.status === 'aberta' || p.status === 'em_andamento') && !abertoPorUc.has(p.uc_id)) {
      abertoPorUc.set(p.uc_id, p);
    }
  }

  return (ucs || []).map((uc): UcParaPedido => {
    const pedido = abertoPorUc.get(uc.id) ?? null;
    if (uc.empresa_parceira_id) {
      return {
        ...uc, podePedir: false, impedimento: 'de_parceira', pedidoEmAberto: pedido,
        aviso: 'O cancelamento desta unidade é feito pela empresa parceira responsável pelo seu contrato. Fale com ela para seguir.',
      };
    }
    if (pedido) {
      return {
        ...uc, podePedir: false, impedimento: 'pedido_em_aberto', pedidoEmAberto: pedido,
        aviso: pedido.protocolo
          ? `Já existe um pedido em andamento para esta unidade, protocolo ${pedido.protocolo}.`
          : 'Já existe um pedido em andamento para esta unidade.',
      };
    }
    if (situacaoEncerrada(uc.situacao)) {
      return {
        ...uc, podePedir: false, impedimento: 'ja_cancelada', pedidoEmAberto: null,
        aviso: 'Esta unidade já está cancelada.',
      };
    }
    return { ...uc, podePedir: true, impedimento: null, aviso: null, pedidoEmAberto: pedido };
  });
}

/** Texto do estado vazio: o motivo muda conforme o porquê da lista estar vazia. */
export function mensagemListaVazia(totalUcs: number, disponiveis: number): string | null {
  if (totalUcs === 0) {
    return 'Não encontramos nenhuma unidade ativa na sua conta. Se você acredita que isso está errado, fale com a gente pelo WhatsApp.';
  }
  if (disponiveis === 0) {
    return 'Nenhuma das suas unidades está disponível para pedir cancelamento agora. Veja o motivo em cada uma abaixo.';
  }
  return null;
}

/** Detalhe obrigatório quando o motivo é "outro" — senão a equipe recebe um pedido sem contexto. */
export function detalheObrigatorio(motivo: MotivoPortal): boolean {
  return motivo === 'outro';
}

export interface ResultadoPedidoPortal {
  ja_existia: boolean;
  id: number;
  protocolo: string | null;
  status: string;
  data_solicitacao: string | null;
  data_retorno_prevista: string | null;
}

export interface SolicitantePortal {
  nome: string;
  cpf: string;
  email: string;
  telefone: string;
  vinculo: string;
  declaraVeracidade: boolean;
}

/** Abre o pedido pelo servidor. A tela nunca escreve na tabela (demanda 02). */
export async function abrirPedidoCancelamentoPortal(
  ucId: number,
  motivo: MotivoPortal,
  detalhe: string,
  solicitante?: SolicitantePortal,
): Promise<ResultadoPedidoPortal> {
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase.rpc('abrir_cancelamento_portal', {
    p_uc_id: ucId,
    p_motivo_categoria: motivo,
    p_motivo_detalhe: detalhe.trim() || null,
    p_solicitante_nome: solicitante?.nome?.trim() || null,
    p_solicitante_cpf: solicitante?.cpf?.trim() || null,
    p_solicitante_email: solicitante?.email?.trim() || null,
    p_solicitante_telefone: solicitante?.telefone?.trim() || null,
    p_solicitante_vinculo: solicitante?.vinculo || null,
    p_declara_veracidade: !!solicitante?.declaraVeracidade,
  });
  if (error) throw error;
  return data as ResultadoPedidoPortal;
}

export interface MeuCancelamento {
  id: number;
  protocolo: string | null;
  uc_id: number;
  uc_numero: string | null;
  status: string;
  motivo_categoria: MotivoPortal;
  data_solicitacao: string | null;
  data_retorno_prevista: string | null;
  respondido: boolean;
}

export async function listarMeusCancelamentos(): Promise<MeuCancelamento[]> {
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase.rpc('meus_cancelamentos_portal');
  if (error) throw error;
  return (data || []) as MeuCancelamento[];
}

/**
 * O endereço da UC vem montado do cadastro e, quando os campos estão em branco,
 * chega como pontuação solta (", - , /"). Mostrar isso ao cliente parece defeito
 * da tela; sem conteúdo de verdade, a linha some.
 */
export function enderecoExibivel(endereco: string | null | undefined): string {
  const texto = String(endereco ?? '').trim();
  return /[0-9A-Za-zÀ-ÿ]/.test(texto) ? texto : '';
}
